import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Home, ShoppingCart, UtensilsCrossed, User, Clock, AlertTriangle, Sandwich, Store, LogOut } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/contexts/CartContext";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { BRAND_COLORS } from "@/lib/menuData";
import PizzaPromoPopup from "@/components/PizzaPromoPopup";
import OrderHistoryDrawer from "@/components/OrderHistoryDrawer";
import WelcomePopup from "@/components/WelcomePopup";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_foodapp-24/artifacts/qn823x2x_PHOTO-2026-02-06-16-32-00.jpg";

const BRANDS = [
  {
    id: "chicken",
    name: "Krispy Krunchy",
    subtitle: "Freshly Made • Perfectly Cajun",
    logo: "https://customer-assets.emergentagent.com/job_foodapp-24/artifacts/2j2q7r5p_KKC-Logo.webp",
    bgColor: "#0D2E4A",
    route: "/order/chicken",
    conceptKey: "chicken",
  },
  {
    id: "pizza",
    name: "Hunt Brothers Pizza",
    subtitle: "Made Fresh Daily",
    logo: "https://customer-assets.emergentagent.com/job_foodapp-24/artifacts/2iv7onaz_hunts.gif",
    bgColor: "#FFFFFF",
    route: "/order/pizza",
    conceptKey: "pizza",
  },
  {
    id: "lunch",
    name: "Daily Specials",
    subtitle: "Island Grill & Deli",
    logo: null,
    bgColor: "#2E7D32",
    route: "/order/lunch",
    conceptKey: "lunch",
    useIcon: true,
    iconType: "lunch",
  },
];

export default function HubPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { itemCount, addItem, clearCart } = useCart();
  const { customer, isAuthenticated } = useCustomerAuth();
  const [showPromo, setShowPromo] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [conceptStatus, setConceptStatus] = useState({});

  // In-store mode detection
  const isInStoreMode = searchParams.get("mode") === "instore";
  const [employeeSession, setEmployeeSession] = useState(null);

  // Check employee session on mount
  useEffect(() => {
    if (isInStoreMode) {
      const session = sessionStorage.getItem("employeeSession");
      if (session) {
        const parsed = JSON.parse(session);
        // Check session timeout (30 minutes)
        if (Date.now() - parsed.loginTime > 30 * 60 * 1000) {
          sessionStorage.removeItem("employeeSession");
          navigate("/employee");
          return;
        }
        setEmployeeSession(parsed);
      } else {
        // Not logged in, redirect to employee login
        navigate("/employee");
      }
    }
  }, [isInStoreMode, navigate]);

  const handleEmployeeLogout = () => {
    sessionStorage.removeItem("employeeSession");
    clearCart();
    navigate("/employee");
  };

  // Fetch concept status
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/concepts/status`);
        setConceptStatus(res.data);
      } catch (e) {
        console.error("Failed to fetch concept status");
      }
    };
    fetchStatus();
    // Refresh every minute
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  // Show promo popup when coming from landing page (not in in-store mode)
  useEffect(() => {
    if (isInStoreMode) return; // Skip promo in in-store mode
    
    // Check if user came from landing page (state or referrer)
    const fromLanding = location.state?.fromLanding || sessionStorage.getItem("showPizzaPromo") === "true";
    
    if (fromLanding) {
      setShowPromo(true);
      // Clear the flag so it doesn't show again on refresh
      sessionStorage.removeItem("showPizzaPromo");
    }
  }, [location, isInStoreMode]);

  const handleClosePromo = () => {
    setShowPromo(false);
  };

  const handleReorder = (items) => {
    // Add all items from previous order to cart
    items.forEach(item => {
      addItem({
        name: item.name,
        concept: item.concept,
        category: item.category,
        price: item.price,
        quantity: item.quantity,
        customizations: item.customizations || {}
      });
    });
    navigate("/cart");
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: BRAND_COLORS.sandWhite }}>
      {/* In-Store Mode Banner */}
      {isInStoreMode && employeeSession && (
        <div 
          className="h-12 flex items-center justify-between px-4"
          style={{ backgroundColor: "#0066cc" }}
          data-testid="instore-banner"
        >
          <div className="flex items-center gap-2">
            <Store className="h-5 w-5 text-white" />
            <span className="text-white font-semibold text-sm">IN-STORE MODE</span>
            <span className="text-white/70 text-sm">|</span>
            <span className="text-white/90 text-sm">{employeeSession.name}</span>
          </div>
          <button
            data-testid="employee-logout-btn"
            className="flex items-center gap-1 text-white/90 hover:text-white text-sm font-medium"
            onClick={handleEmployeeLogout}
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      )}

      {/* Header */}
      <header 
        className="h-14 flex items-center justify-between px-4"
        style={{ backgroundColor: BRAND_COLORS.deepTeal }}
      >
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Logo" className="h-9 w-9 rounded-full object-cover" />
          <div>
            <h1 className="text-sm font-bold text-white" style={{ fontFamily: "Montserrat" }}>
              Dauphin Island Food Mart
            </h1>
            <Badge 
              className="text-[9px] font-medium px-1.5 py-0" 
              style={{ backgroundColor: BRAND_COLORS.tropicalAqua, color: "white" }}
            >
              Pick-up / To-Go
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Account Button - shows for logged-in customers */}
          {isAuthenticated && customer && (
            <Button 
              data-testid="account-btn"
              variant="outline" 
              size="sm" 
              className="rounded-full h-10 w-10 p-0 border-2"
              style={{ borderColor: BRAND_COLORS.tropicalAqua, backgroundColor: "transparent" }}
              onClick={() => setShowAccount(true)}
            >
              {customer.picture ? (
                <img src={customer.picture} alt="" className="h-8 w-8 rounded-full" />
              ) : (
                <User className="h-5 w-5 text-white" />
              )}
            </Button>
          )}
          <Button 
            data-testid="hub-cart-btn"
            variant="outline" 
            size="sm" 
            className="relative rounded-full h-10 w-10 p-0 border-2"
            style={{ borderColor: BRAND_COLORS.tropicalAqua, backgroundColor: "transparent" }}
            onClick={() => navigate("/cart")}
          >
            <ShoppingCart className="h-5 w-5 text-white" />
            {itemCount > 0 && (
              <span 
                className="absolute -top-1 -right-1 text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center"
                style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}
              >
                {itemCount}
              </span>
            )}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h2 
            className="text-2xl sm:text-3xl font-extrabold mb-2"
            style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}
          >
            What are you craving?
          </h2>
          <p className="text-sm" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
            Choose a brand to start your order
          </p>
        </div>

        {/* Brand Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {BRANDS.map((brand) => {
            const status = brand.conceptKey ? conceptStatus[brand.conceptKey]?.currentStatus : null;
            const isOpen = status ? status.isOpen : true;
            const closesIn = status?.closesIn;
            const closingSoon = closesIn && closesIn <= 30;

            // Handle brand click - navigate to menu (preserve mode)
            const handleBrandClick = () => {
              if (!isOpen) return;
              const route = isInStoreMode ? `${brand.route}?mode=instore` : brand.route;
              navigate(route);
            };

            return (
              <button
                key={brand.id}
                data-testid={`brand-card-${brand.id}`}
                onClick={handleBrandClick}
                disabled={!isOpen}
                className={`group rounded-2xl overflow-hidden shadow-md transition-all duration-300 relative ${
                  isOpen 
                    ? "hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]" 
                    : "opacity-60 cursor-not-allowed"
                }`}
                style={{ backgroundColor: brand.bgColor }}
              >
                {/* Closed Overlay */}
                {!isOpen && (
                  <div className="absolute inset-0 z-10 bg-slate-900/70 flex flex-col items-center justify-center p-4">
                    <Clock className="w-8 h-8 text-white mb-2" />
                    <p className="text-white font-bold text-sm text-center">Currently Closed</p>
                    {status?.customMessage && (
                      <p className="text-white/80 text-xs text-center mt-1">{status.customMessage}</p>
                    )}
                    {status?.nextOpen && (
                      <p className="text-white/60 text-xs text-center mt-2">Opens {status.nextOpen}</p>
                    )}
                  </div>
                )}

                {/* Closing Soon Warning */}
                {isOpen && closingSoon && (
                  <div className="absolute top-2 left-2 right-2 z-10 bg-amber-500 rounded-lg px-2 py-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3 text-white" />
                    <span className="text-[10px] text-white font-semibold">
                      Closes in {closesIn} min - Order now!
                    </span>
                  </div>
                )}

                <div className="aspect-square flex items-center justify-center p-6">
                  {brand.useIcon ? (
                    brand.iconType === "lunch" ? (
                      <Sandwich className="w-20 h-20 text-white" />
                    ) : (
                      <UtensilsCrossed className="w-20 h-20 text-white" />
                    )
                  ) : (
                    <img 
                      src={brand.logo} 
                      alt={brand.name} 
                      className="max-w-full max-h-full object-contain"
                    />
                  )}
                </div>
                <div 
                  className="p-4 text-center"
                  style={{ backgroundColor: BRAND_COLORS.deepTeal }}
                >
                  <h3 className="font-bold text-white text-base" style={{ fontFamily: "Montserrat" }}>
                    {brand.name}
                  </h3>
                  {isOpen && status?.closeTime ? (
                    <p className="text-xs text-white/70 mt-1">Open until {status.closeTime}</p>
                  ) : (
                    <p className="text-xs text-white/70 mt-1">{brand.subtitle}</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Back to Landing */}
        <div className="text-center mt-8">
          <Button
            data-testid="back-to-landing-btn"
            variant="ghost"
            className="text-sm font-medium"
            style={{ color: BRAND_COLORS.darkSlate + "99" }}
            onClick={() => navigate("/")}
          >
            <Home className="h-4 w-4 mr-1" /> Back to Welcome
          </Button>
        </div>
      </main>

      {/* Footer */}
      <Footer />

      {/* Floating cart FAB removed — the sticky header cart button (with badge) is sufficient
          and keeping both creates duplicate UI that covers the footer on mobile. */}

      {/* Pizza Promo Popup */}
      <PizzaPromoPopup show={showPromo} onClose={handleClosePromo} />

      {/* Order History Drawer */}
      <OrderHistoryDrawer 
        open={showAccount} 
        onClose={() => setShowAccount(false)}
        onReorder={handleReorder}
      />

      {/* Welcome Popup for New Customers (not in in-store mode) */}
      {!isInStoreMode && <WelcomePopup />}
    </div>
  );
}
